import type {
  DiagnosisAnswer,
  DiagnosisConfig,
  DiagnosisSession,
  IssueDefinition,
  OptionDefinition,
  OutcomeDefinition,
} from './types';

export function validateConfig(config: DiagnosisConfig): void {
  const issueIds = new Set<string>();
  const reachableNodes = new Set<string>();

  for (const issue of config.issues) {
    if (issueIds.has(issue.id)) {
      throw new Error(`Duplicate issue id: ${issue.id}`);
    }
    issueIds.add(issue.id);
    if (!config.nodes[issue.startNodeId]) {
      throw new Error(`Missing start node ${issue.startNodeId} for issue ${issue.id}`);
    }
    walkNodes(config, issue.startNodeId, reachableNodes, new Set());
  }

  for (const node of Object.values(config.nodes)) {
    if (!reachableNodes.has(node.id)) {
      throw new Error(`Unreachable node: ${node.id}`);
    }
    if (node.options.length === 0) {
      throw new Error(`Node has no options: ${node.id}`);
    }
    for (const option of node.options) {
      const targetCount = Number(Boolean(option.nextNodeId)) + Number(Boolean(option.outcomeId));
      if (targetCount !== 1) {
        throw new Error(`Option ${node.id}/${option.value} must have exactly one target`);
      }
      if (option.outcomeId && !config.outcomes[option.outcomeId]) {
        throw new Error(`Missing outcome ${option.outcomeId} from node ${node.id}`);
      }
    }
  }

  for (const outcome of Object.values(config.outcomes)) {
    if (outcome.kind === 'self_action' && !outcome.fallbackOutcomeId) {
      throw new Error(`Self-action outcome ${outcome.id} must define a fallback`);
    }
    if (!outcome.fallbackOutcomeId) {
      continue;
    }

    const fallbackOutcome = config.outcomes[outcome.fallbackOutcomeId];
    if (!fallbackOutcome) {
      throw new Error(`Missing fallback outcome ${outcome.fallbackOutcomeId} from outcome ${outcome.id}`);
    }
    if (fallbackOutcome.id === outcome.id) {
      throw new Error(`Outcome ${outcome.id} cannot fall back to itself`);
    }
    if (fallbackOutcome.kind === 'self_action') {
      throw new Error(`Fallback outcome ${fallbackOutcome.id} must be terminal`);
    }
  }
}

function walkNodes(
  config: DiagnosisConfig,
  nodeId: string,
  reachable: Set<string>,
  activePath: Set<string>,
): void {
  if (activePath.has(nodeId)) {
    throw new Error(`Diagnosis cycle detected at node: ${nodeId}`);
  }
  if (reachable.has(nodeId)) {
    return;
  }

  const node = config.nodes[nodeId];
  if (!node) {
    throw new Error(`Missing node: ${nodeId}`);
  }

  reachable.add(nodeId);
  const nextPath = new Set(activePath).add(nodeId);
  for (const option of node.options) {
    if (option.nextNodeId) {
      walkNodes(config, option.nextNodeId, reachable, nextPath);
    }
  }
}

export function getIssue(config: DiagnosisConfig, issueId: string): IssueDefinition | undefined {
  return config.issues.find((issue) => issue.id === issueId);
}

export function createSession(issue: IssueDefinition): DiagnosisSession {
  return {
    version: 1,
    issueId: issue.id,
    currentNodeId: issue.startNodeId,
    answers: [],
  };
}

export function chooseOption(
  config: DiagnosisConfig,
  session: DiagnosisSession,
  optionValue: string,
): DiagnosisSession {
  if (!session.currentNodeId) {
    throw new Error('Diagnosis is already complete');
  }

  const node = config.nodes[session.currentNodeId];
  if (!node) {
    throw new Error(`Unknown current node: ${session.currentNodeId}`);
  }

  const option = node.options.find((candidate) => candidate.value === optionValue);
  if (!option) {
    throw new Error(`Unknown option ${optionValue} for node ${node.id}`);
  }

  const answer: DiagnosisAnswer = {
    nodeId: node.id,
    value: option.value,
    label: option.label,
    summaryKey: node.summaryKey,
  };

  return {
    ...session,
    currentNodeId: option.nextNodeId,
    outcomeId: option.outcomeId,
    answers: [...session.answers, answer],
  };
}

export function stepBack(config: DiagnosisConfig, session: DiagnosisSession): DiagnosisSession {
  const issue = getIssue(config, session.issueId);
  if (!issue || session.answers.length === 0) {
    return session;
  }

  const retainedAnswers = session.answers.slice(0, -1);
  let rebuilt = createSession(issue);
  for (const answer of retainedAnswers) {
    rebuilt = chooseOption(config, rebuilt, answer.value);
  }
  return rebuilt;
}

export function getCurrentOutcome(
  config: DiagnosisConfig,
  session: DiagnosisSession,
): OutcomeDefinition | undefined {
  return session.outcomeId ? config.outcomes[session.outcomeId] : undefined;
}

export function escalateOutcome(
  config: DiagnosisConfig,
  session: DiagnosisSession,
): DiagnosisSession {
  const outcome = getCurrentOutcome(config, session);
  if (!outcome) {
    throw new Error('Diagnosis has no outcome to escalate');
  }
  if (!outcome.fallbackOutcomeId) {
    throw new Error(`Outcome ${outcome.id} has no fallback`);
  }
  if (!config.outcomes[outcome.fallbackOutcomeId]) {
    throw new Error(`Unknown fallback outcome: ${outcome.fallbackOutcomeId}`);
  }

  return {
    ...session,
    currentNodeId: undefined,
    outcomeId: outcome.fallbackOutcomeId,
  };
}

export function getOptionByValue(
  config: DiagnosisConfig,
  nodeId: string,
  value: string,
): OptionDefinition | undefined {
  return config.nodes[nodeId]?.options.find((option) => option.value === value);
}
